import { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { openTanya } from "@/hooks/useTanyaTrigger";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import tangoLogo from "@/assets/brand/TangoExtend_Horizontal_Blue.svg";

/* ── Fallback hardcoded links (used if DB fetch fails or returns empty) ── */
const FALLBACK_GROUPS = [
  { label: "Tango Extend", columns: 1, alignRight: false, links: [
    { title: "Tango Extend", href: "/tango-extend", description: "Global mobile service integrated with UC platforms", is_external: false },
    { title: "Enterprise Text Messaging", href: "/solutions/enterprise-text-messaging", description: "SMS/MMS with UC integration", is_external: false },
  ]},
  { label: "Solutions", columns: 2, alignRight: false, links: [
    { title: "All Solutions", href: "/solutions/all", description: "Browse all solution pillars", is_external: false },
    { title: "One Voice", href: "/solutions/p/one-voice", description: "Better Business is Simpler", is_external: false },
    { title: "Sustainability", href: "/solutions/p/sustainability", description: "Better Business is Responsible", is_external: false },
    { title: "Control", href: "/solutions/p/control", description: "Better Business is Controlled", is_external: false },
    { title: "Compliance", href: "/solutions/p/compliance", description: "Better Business is Accountable", is_external: false },
    { title: "Your People", href: "/solutions/p/your-people", description: "Better Business Feels Better", is_external: false },
    { title: "Your Customers", href: "/solutions/p/your-customers", description: "Better Business Looks Better", is_external: false },
    { title: "Always On", href: "/solutions/p/always-on", description: "Better Business Never Stops", is_external: false },
    { title: "Work Anywhere", href: "/solutions/p/work-anywhere", description: "Better Business Travels With You", is_external: false },
    { title: "AI & Intelligence", href: "/solutions/p/ai-intelligence", description: "Better Business Gets Smarter", is_external: false },
  ]},
  { label: "Industries", columns: 1, alignRight: false, links: [
    { title: "All Industries", href: "/solutions/industries", description: "Browse all industry solutions", is_external: false },
    { title: "Healthcare", href: "/solutions/p/healthcare-mobile-communications", description: "Secure, compliant mobile comms for clinical teams", is_external: false },
    { title: "Finance", href: "/solutions/p/finance-mobile-communications", description: "Recorded, compliant mobile comms for financial teams", is_external: false },
  ]},
  { label: "Service", columns: 1, alignRight: false, links: [
    { title: "Global Coverage", href: "/country-availability", description: "Countries and regions we serve", is_external: false },
    { title: "Supported Devices", href: "/supported-phones", description: "Compatible phones and devices", is_external: false },
    { title: "Subscription Plans", href: "/plans", description: "Plans for smartphones and fixed devices", is_external: false },
    { title: "eSIM Activation", href: "/products/esim", description: "Quick and easy eSIM deployment", is_external: false },
    { title: "Value Calculator", href: "/value-calculator", description: "Calculate your potential savings", is_external: false },
  ]},
  { label: "How to Buy", columns: 1, alignRight: true, links: [
    { title: "Where to Buy", href: "/where-to-buy", description: "Find a Tango Networks partner", is_external: false },
    { title: "Partner Program", href: "/partners", description: "Become a Tango Networks partner", is_external: false },
    { title: "Contact Sales", href: "/contact", description: "Speak with our sales team", is_external: false },
  ]},
  { label: "About", columns: 1, alignRight: true, links: [
    { title: "About Us", href: "/about", description: "Learn about Tango Networks", is_external: false },
    { title: "News", href: "/news", description: "Latest company news and press", is_external: false },
    { title: "Blog", href: "/blog", description: "Industry insights and updates", is_external: false },
    { title: "Resources", href: "/resources", description: "Solution notes, guides, and datasheets", is_external: false },
    { title: "Frost & Sullivan Award", href: "/frost-sullivan-award", description: "Industry recognition", is_external: false },
    { title: "Subscribe", href: "/subscribe", description: "Get updates from Tango Networks", is_external: false },
  ]},
];

interface NavLinkItem { title: string; href: string; description: string; is_external: boolean }
interface NavGroup { label: string; columns: number; alignRight: boolean; links: NavLinkItem[] }

function useNavLinks() {
  return useQuery({
    queryKey: ["nav_links"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nav_links")
        .select("group_label, title, href, description, sort_order, group_sort_order, columns, align_right, is_external")
        .eq("active", true)
        .order("group_sort_order")
        .order("sort_order");
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

function DropdownMenu({ label, links, columns = 1, alignRight = false }: { label: string; links: NavLinkItem[]; columns?: number; alignRight?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        className="inline-flex h-10 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground transition-colors hover:text-accent focus:outline-none"
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        {label}
        <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className={`absolute top-full z-50 pt-2 ${alignRight ? "right-0" : "left-0"}`} style={{ width: columns === 2 ? "500px" : "280px" }}>
          <div className="rounded-lg border bg-popover text-popover-foreground shadow-xl animate-slide-up">
            <ul className={`grid gap-1 p-3 ${columns === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
              {links.map((link) => {
                const isExt = link.is_external;
                const Wrapper = isExt ? "a" : Link;
                const wrapperProps = isExt
                  ? { href: link.href, target: "_blank", rel: "noopener noreferrer", onClick: () => setOpen(false) }
                  : { to: link.href, onClick: () => setOpen(false) };
                return (
                  <li key={link.title}>
                    <Wrapper
                      {...(wrapperProps as any)}
                      className="block select-none rounded-lg p-3 leading-none no-underline outline-none transition-all duration-200 hover:bg-accent hover:!text-white group hover:translate-x-1"
                    >
                      <div className="text-sm font-semibold leading-none flex items-center gap-1">
                        {link.title}
                        {isExt && <ExternalLink className="w-3 h-3 opacity-60" />}
                      </div>
                      <p className="mt-1 line-clamp-2 text-sm leading-snug text-muted-foreground group-hover:!text-white/80">
                        {link.description}
                      </p>
                    </Wrapper>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { data: rawLinks } = useNavLinks();

  // Lock background scroll while the mobile menu is open (fixes iOS Safari
  // background-scroll bleed and nested-scroll issues inside sticky parents).
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [mobileMenuOpen]);

  const navGroups: NavGroup[] = useMemo(() => {
    if (!rawLinks || rawLinks.length === 0) return FALLBACK_GROUPS;

    const map = new Map<string, NavGroup>();
    for (const row of rawLinks) {
      let g = map.get(row.group_label);
      if (!g) {
        g = { label: row.group_label, columns: row.columns, alignRight: row.align_right, links: [] };
        map.set(row.group_label, g);
      }
      g.links.push({ title: row.title, href: row.href, description: row.description, is_external: row.is_external });
    }
    return Array.from(map.values());
  }, [rawLinks]);

  return (
    <header className="w-full border-b bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80">
      {/* Anniversary Banner */}
      <div className="bg-primary text-primary-foreground py-2 text-center text-sm font-medium">
        <span>Celebrating 20 Years of Innovation in Enterprise Mobility</span>
        <Link to="/about" className="ml-2 inline-flex items-center rounded-md bg-primary-foreground px-3 py-1 text-xs font-bold text-primary hover:bg-primary-foreground/90 transition-colors">
          Learn More &gt;
        </Link>
      </div>

      <div className="container-padding mx-auto">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <img src={tangoLogo} alt="Tango Networks" className="h-[30px] w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navGroups.map((g) => (
              <DropdownMenu key={g.label} label={g.label} links={g.links} columns={g.columns} alignRight={g.alignRight} />
            ))}
          </nav>

          {/* Contact Button */}
          <div className="hidden lg:flex items-center space-x-3">
            <Button variant="default" onClick={() => openTanya("contact-us")}>
              Contact Us
            </Button>
            <Button asChild variant="ghost" size="sm">
              <a href="https://exchange.tango-networks.com/login" target="_blank" rel="noopener noreferrer">
                Partner Portal <ExternalLink className="ml-1 h-3.5 w-3.5" />
              </a>
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="lg:hidden -m-2.5 inline-flex items-center justify-center rounded-md p-2.5"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <span className="sr-only">Toggle menu</span>
            {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile menu — rendered as a fixed overlay drawer so it escapes the
          sticky header parent (iOS Safari can't reliably scroll a child
          inside a position:sticky ancestor). */}
      {mobileMenuOpen && typeof document !== "undefined" && createPortal(
        <div className="lg:hidden fixed inset-0 z-[100] flex flex-col bg-background animate-slide-up">
          <div className="flex h-16 shrink-0 items-center justify-between border-b px-4">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center">
              <img src={tangoLogo} alt="Tango Networks" className="h-[26px] w-auto" />
            </Link>
            <button
              type="button"
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div
            className="flex-1 overflow-y-auto overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch" } as React.CSSProperties}
          >
            <div className="space-y-1 px-4 pb-8 pt-2">
              {navGroups.map((g) => (
                <MobileNavGroup key={g.label} group={g} onNavigate={() => setMobileMenuOpen(false)} />
              ))}
              <div className="pt-4 space-y-2">
                <Button className="w-full" onClick={() => { setMobileMenuOpen(false); openTanya("contact-us"); }}>
                  Contact Us
                </Button>
                <Button asChild variant="ghost" className="w-full" onClick={() => setMobileMenuOpen(false)}>
                  <a href="https://exchange.tango-networks.com/login" target="_blank" rel="noopener noreferrer">
                    Partner Portal <ExternalLink className="ml-1 h-3.5 w-3.5" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </header>
  );
}

function MobileNavGroup({ group, onNavigate }: { group: NavGroup; onNavigate: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border/50 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-base font-semibold hover:bg-muted transition-colors"
        aria-expanded={open}
      >
        <span>{group.label}</span>
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul className="pb-2 pl-3 pr-2 space-y-1 animate-slide-up">
          {group.links.map((link) => {
            const isExt = link.is_external;
            const Wrapper: any = isExt ? "a" : Link;
            const wrapperProps = isExt
              ? { href: link.href, target: "_blank", rel: "noopener noreferrer", onClick: onNavigate }
              : { to: link.href, onClick: onNavigate };
            return (
              <li key={link.title}>
                <Wrapper
                  {...wrapperProps}
                  className="block rounded-md px-3 py-2 text-sm hover:bg-muted transition-colors"
                >
                  <div className="font-medium flex items-center gap-1">
                    {link.title}
                    {isExt && <ExternalLink className="w-3 h-3 opacity-60" />}
                  </div>
                  {link.description && (
                    <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{link.description}</p>
                  )}
                </Wrapper>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
